import React, { useState, useRef, useCallback, useEffect } from 'react';
import { format, isValid, parseISO } from 'date-fns';
import { Icon } from './Icon';
import Button from './Button';
import { useClickOutside } from '../../../hooks/useClickOutside';
import '../../../styles/components/date-range-picker.css';

export interface DateRangePickerProps {
  /** Start date of the range */
  startDate: Date | null;
  /** End date of the range */
  endDate: Date | null;
  /** Callback when dates change */
  onChange: (start: Date | null, end: Date | null) => void;
  /** Placeholder text when no dates selected */
  placeholder?: string;
  /** Minimum selectable date */
  minDate?: Date;
  /** Maximum selectable date */
  maxDate?: Date;
  /** Disable the component */
  disabled?: boolean;
  /** Additional CSS class */
  className?: string;
  /** Label for the component */
  label?: string;
  /** Helper text */
  helper?: string;
  /** Error message */
  error?: string;
}

/**
 * DateRangePicker - A component for selecting a date range
 *
 * @example
 * ```tsx
 * <DateRangePicker
 *   startDate={filters.dateRange.start}
 *   endDate={filters.dateRange.end}
 *   onChange={(start, end) => setFilters({ ...filters, dateRange: { start, end } })}
 *   minDate={new Date()} // Only future dates
 * />
 * ```
 */
export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onChange,
  placeholder = 'Select dates...',
  minDate,
  maxDate,
  disabled = false,
  className = '',
  label,
  helper,
  error
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempStart, setTempStart] = useState<Date | null>(startDate);
  const [tempEnd, setTempEnd] = useState<Date | null>(endDate);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Sync temp values when props change
  useEffect(() => {
    setTempStart(startDate);
    setTempEnd(endDate);
  }, [startDate, endDate]);

  // Close on click outside
  useClickOutside(dropdownRef, () => setIsOpen(false), isOpen);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setTempStart(startDate);
      setTempEnd(endDate);
    }
  }, [startDate, endDate]);

  const handleApply = useCallback(() => {
    onChange(tempStart, tempEnd);
    setIsOpen(false);
  }, [tempStart, tempEnd, onChange]);

  const handleClear = useCallback(() => {
    setTempStart(null);
    setTempEnd(null);
    onChange(null, null);
    setIsOpen(false);
  }, [onChange]);

  const handleClearInline = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    handleClear();
  }, [handleClear]);

  const handleStartChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value) {
      const date = parseISO(value);
      if (isValid(date)) {
        setTempStart(date);
        // Auto-adjust end date if needed
        if (tempEnd && date > tempEnd) {
          setTempEnd(date);
        }
      }
    } else {
      setTempStart(null);
    }
  }, [tempEnd]);

  const handleEndChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value) {
      const date = parseISO(value);
      if (isValid(date)) {
        setTempEnd(date);
        // Auto-adjust start date if needed
        if (tempStart && date < tempStart) {
          setTempStart(date);
        }
      }
    } else {
      setTempEnd(null);
    }
  }, [tempStart]);

  const formatRange = useCallback(() => {
    if (!startDate && !endDate) return placeholder;
    if (startDate && !endDate) return `From ${format(startDate, 'MMM d')}`;
    if (!startDate && endDate) return `Until ${format(endDate, 'MMM d')}`;

    // If same year, show year only at the end
    const startYear = startDate!.getFullYear();
    const endYear = endDate!.getFullYear();

    if (startYear === endYear) {
      return `${format(startDate!, 'MMM d')} - ${format(endDate!, 'MMM d, yyyy')}`;
    }
    return `${format(startDate!, 'MMM d, yyyy')} - ${format(endDate!, 'MMM d, yyyy')}`;
  }, [startDate, endDate, placeholder]);

  const formatDateForInput = (date: Date | null): string => {
    if (!date || !isValid(date)) return '';
    return format(date, 'yyyy-MM-dd');
  };

  const hasValue = startDate || endDate;
  const containerClasses = [
    'date-range-picker',
    disabled && 'date-range-picker--disabled',
    error && 'date-range-picker--error',
    className
  ].filter(Boolean).join(' ');

  return (
    <div
      className={containerClasses}
      ref={dropdownRef}
      onKeyDown={handleKeyDown}
    >
      {label && (
        <label className="date-range-picker__label">{label}</label>
      )}

      {/* Trigger Button */}
      <button
        type="button"
        className={`date-range-picker__trigger ${hasValue ? 'date-range-picker__trigger--has-value' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={`Date range: ${formatRange()}`}
      >
        <Icon name="Calendar" size={16} className="date-range-picker__icon" />
        <span className="date-range-picker__text">{formatRange()}</span>
        <Icon
          name="ChevronDown"
          size={16}
          className={`date-range-picker__chevron ${isOpen ? 'date-range-picker__chevron--open' : ''}`}
        />
        {hasValue && !disabled && (
          <button
            type="button"
            className="date-range-picker__clear-inline"
            onClick={handleClearInline}
            aria-label="Clear date range"
          >
            <Icon name="X" size={14} />
          </button>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div
          className="date-range-picker__dropdown"
          role="dialog"
          aria-label="Date range selector"
        >
          <div className="date-range-picker__inputs">
            <div className="date-range-picker__field">
              <label htmlFor="date-range-start">From</label>
              <input
                id="date-range-start"
                type="date"
                value={formatDateForInput(tempStart)}
                onChange={handleStartChange}
                min={minDate ? formatDateForInput(minDate) : undefined}
                max={tempEnd ? formatDateForInput(tempEnd) : maxDate ? formatDateForInput(maxDate) : undefined}
              />
            </div>
            <div className="date-range-picker__field">
              <label htmlFor="date-range-end">To</label>
              <input
                id="date-range-end"
                type="date"
                value={formatDateForInput(tempEnd)}
                onChange={handleEndChange}
                min={tempStart ? formatDateForInput(tempStart) : minDate ? formatDateForInput(minDate) : undefined}
                max={maxDate ? formatDateForInput(maxDate) : undefined}
              />
            </div>
          </div>

          <div className="date-range-picker__actions">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              type="button"
            >
              Clear
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleApply}
              type="button"
            >
              Apply
            </Button>
          </div>
        </div>
      )}

      {/* Helper or Error text */}
      {(helper || error) && (
        <span className={`date-range-picker__helper ${error ? 'date-range-picker__helper--error' : ''}`}>
          {error || helper}
        </span>
      )}
    </div>
  );
};

export default DateRangePicker;
